import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import { useArticles, useArticleCount, useArticleCategories, useArticleFamilies, useArticleBrands, useDeleteArticle } from '@/hooks/use-articles';
import { useStorefrontProducts } from '@/hooks/use-storefront';
import { useAllStock, useWarehouses } from '@/hooks/use-stock';
import { useOpenCreateFromQuery } from '@/hooks/use-open-create-from-query';
import { useDataService } from '@/lib/service-context';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import type { Article, ArticleFilters, ArticleStock } from '@erp/data-access/interfaces';
import { ArticleFormDialog } from './ArticleFormDialog';
import { ArticleTicketPrintDialog } from './ArticleTicketPrintDialog';
import {
  Badge, Button, Card, CardContent, CardHeader, Checkbox,
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  EmptyState, FilterBar, FilterField, Input, PageHeader,
  Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@erp/ui';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, SlidersHorizontal, GripVertical, Eye, EyeOff, RotateCcw, Package, Printer, X, ChevronLeft, ChevronRight } from '@/components/icons';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ColumnTextFilterFields, useColumnTextFilters, type ColumnTextFilter } from '@/components/ui/column-text-filters';

function formatPrice(n: number | null | undefined): string {
  // Optimistic rows from the offline outbox can land here without a
  // sale price set yet — render an em dash instead of crashing.
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

interface ArticleColumnDef {
  key: string;
  labelKey: string;
  locked?: boolean;
}

const ALL_ARTICLE_COLUMNS: ArticleColumnDef[] = [
  { key: 'image', labelKey: 'articles.image' },
  { key: 'code', labelKey: 'articles.code', locked: true },
  { key: 'name', labelKey: 'articles.name' },
  { key: 'barcode', labelKey: 'articles.barcode' },
  { key: 'category', labelKey: 'articles.category' },
  { key: 'subCategory', labelKey: 'articles.subCategory' },
  { key: 'family', labelKey: 'articles.family' },
  { key: 'brand', labelKey: 'articles.brand' },
  { key: 'salePriceHt', labelKey: 'articles.salePriceHt' },
  { key: 'salePriceTtc', labelKey: 'articles.salePriceTtc' },
  { key: 'purchasePriceHt', labelKey: 'articles.purchasePriceHt' },
  { key: 'supplierDiscount', labelKey: 'articles.supplierDiscount' },
  { key: 'vatRate', labelKey: 'articles.vatRate' },
  { key: 'margin', labelKey: 'articles.margin' },
  { key: 'benefit', labelKey: 'articles.benefitAmount' },
  { key: 'unit', labelKey: 'articles.unit' },
  { key: 'stock', labelKey: 'stock.currentStock' },
  { key: 'isPublishedStorefront', labelKey: 'articles.isPublishedStorefront' },
  { key: 'isPublishedPro', labelKey: 'articles.isPublishedPro' },
  { key: 'isActive', labelKey: 'articles.isActive' },
  { key: 'isInvoiceable', labelKey: 'articles.isInvoiceable' },
];

const DEFAULT_ARTICLE_COLS = ['image', 'code', 'name', 'category', 'salePriceHt', 'vatRate', 'stock', 'isPublishedStorefront', 'isPublishedPro', 'isActive'];
const STORAGE_KEY = 'erp_article_columns';
const PUBLICATION_COLS_MIGRATION_KEY = `${STORAGE_KEY}_publication_cols_v1`;
const ARTICLE_LIST_PAGE_SIZE = 120;

function normalizeArticleCols(cols: string[], includePublicationCols: boolean): string[] {
  const valid = new Set(ALL_ARTICLE_COLUMNS.map((c) => c.key));
  const cleaned = cols.filter((key, index) => valid.has(key) && cols.indexOf(key) === index);
  if (!includePublicationCols) return cleaned.length > 0 ? cleaned : DEFAULT_ARTICLE_COLS;

  const next = [...(cleaned.length > 0 ? cleaned : DEFAULT_ARTICLE_COLS)];
  for (const key of ['isPublishedStorefront', 'isPublishedPro']) {
    if (next.includes(key)) continue;
    const insertBefore = next.indexOf('isActive');
    if (insertBefore >= 0) next.splice(insertBefore, 0, key);
    else next.push(key);
  }
  return next;
}

function loadArticleCols(): string[] {
  const shouldMigrate = !localStorage.getItem(PUBLICATION_COLS_MIGRATION_KEY);
  try {
    const r = localStorage.getItem(STORAGE_KEY);
    if (r) return normalizeArticleCols(JSON.parse(r), shouldMigrate);
  } catch {}
  return DEFAULT_ARTICLE_COLS;
}
function saveArticleColsLocal(cols: string[]) { localStorage.setItem(STORAGE_KEY, JSON.stringify(cols)); }

export function ArticlesPage() {
  const { t } = useTranslation();
  const navigate = useSmartNavigate();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [familyFilter, setFamilyFilter] = useState<string>('');
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>('');
  const [invoiceableFilter, setInvoiceableFilter] = useState<string>('');
  const [formOpen, setFormOpen] = useState(false);
  const [editArticle, setEditArticle] = useState<Article | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState<Article | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [articlesToPrint, setArticlesToPrint] = useState<Article[]>([]);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search.trim(), 180);

  const { data: categories } = useArticleCategories();
  const { data: families } = useArticleFamilies();
  const { data: brands } = useArticleBrands();

  // Legacy/imported rows may store the family/brand as the raw name string
  // while the dropdown emits the UUID. Pass both candidates so the
  // repository can match either representation. See ArticleFilters.
  const familyFilterCandidates = useMemo<string[] | undefined>(() => {
    if (!familyFilter) return undefined;
    const f = families?.find((x) => x.id === familyFilter);
    return f ? [f.id, f.name] : [familyFilter];
  }, [familyFilter, families]);
  const brandFilterCandidates = useMemo<string[] | undefined>(() => {
    if (!brandFilter) return undefined;
    const b = brands?.find((x) => x.id === brandFilter);
    return b ? [b.id, b.name] : [brandFilter];
  }, [brandFilter, brands]);

  const countFilters: ArticleFilters = useMemo(() => ({
    search: debouncedSearch || undefined,
    categoryId: categoryFilter || undefined,
    family: familyFilterCandidates,
    brand: brandFilterCandidates,
    isActive: activeFilter === '' ? undefined : activeFilter === 'true',
    isInvoiceable: invoiceableFilter === '' ? undefined : invoiceableFilter === 'true',
  }), [debouncedSearch, categoryFilter, familyFilterCandidates, brandFilterCandidates, activeFilter, invoiceableFilter]);

  const filters: ArticleFilters = useMemo(() => ({
    ...countFilters,
    offset: (page - 1) * ARTICLE_LIST_PAGE_SIZE,
    limit: ARTICLE_LIST_PAGE_SIZE,
  }), [countFilters, page]);

  const {
    data: rawArticles,
    isLoading,
    isFetching,
    isError,
    isPlaceholderData,
  } = useArticles(filters);
  const {
    data: totalArticleCount,
    isLoading: isCountLoading,
    isFetching: isCountFetching,
    isError: isCountError,
    isPlaceholderData: isCountPlaceholderData,
  } = useArticleCount(countFilters);
  const { data: allStock } = useAllStock();
  const { data: warehouses } = useWarehouses();
  const deleteMutation = useDeleteArticle();
  const ds = useDataService();
  const { data: storefrontProducts } = useStorefrontProducts();
  const { data: proArticlePricing } = useQuery({
    queryKey: ['pro-article-pricing'],
    queryFn: () => ds.proArticlePricing.listAll(),
  });

  const [visibleCols, setVisibleCols] = useState<string[]>(loadArticleCols);
  const [colConfigOpen, setColConfigOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  // Load from backend on mount
  useEffect(() => {
    const shouldMigrate = !localStorage.getItem(PUBLICATION_COLS_MIGRATION_KEY);
    ds.userPreferences.get(STORAGE_KEY).then((val) => {
      if (val && Array.isArray(val) && val.length > 0) {
        const cols = normalizeArticleCols(val as string[], shouldMigrate);
        setVisibleCols(cols);
        saveArticleColsLocal(cols);
        if (shouldMigrate && JSON.stringify(cols) !== JSON.stringify(val)) {
          ds.userPreferences.set(STORAGE_KEY, cols).catch(() => {});
        }
      } else if (shouldMigrate) {
        setVisibleCols((prev) => {
          const cols = normalizeArticleCols(prev, true);
          saveArticleColsLocal(cols);
          ds.userPreferences.set(STORAGE_KEY, cols).catch(() => {});
          return cols;
        });
      }
    }).catch(() => {}).finally(() => {
      if (shouldMigrate) localStorage.setItem(PUBLICATION_COLS_MIGRATION_KEY, '1');
    });
  }, [ds]);

  const saveArticleCols = (cols: string[]) => {
    saveArticleColsLocal(cols);
    ds.userPreferences.set(STORAGE_KEY, cols).catch(() => {});
  };

  const toggleCol = (key: string) => {
    setVisibleCols((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      saveArticleCols(next);
      return next;
    });
  };
  const resetCols = () => { setVisibleCols(DEFAULT_ARTICLE_COLS); saveArticleCols(DEFAULT_ARTICLE_COLS); };
  const moveCol = (from: number, to: number) => {
    setVisibleCols((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved!);
      saveArticleCols(next);
      return next;
    });
  };

  const hasMultipleWarehouses = (warehouses?.length ?? 0) > 1;

  const categoryMap = useMemo(() => {
    const map = new Map<string, string>();
    categories?.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [categories]);

  const pageArticles = rawArticles ?? [];
  const totalArticles = totalArticleCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalArticles / ARTICLE_LIST_PAGE_SIZE));
  const pageStart = totalArticles === 0 ? 0 : (page - 1) * ARTICLE_LIST_PAGE_SIZE + 1;
  const pageEnd = Math.min(page * ARTICLE_LIST_PAGE_SIZE, totalArticles);
  const isCheckingArticles = isLoading || isCountLoading || rawArticles === undefined || totalArticleCount === undefined || isPlaceholderData || isCountPlaceholderData;
  const hasLoadError = isError || isCountError;

  useEffect(() => {
    setPage(1);
  }, [countFilters]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const goToPage = (nextPage: number) => {
    setSelectedIds(new Set());
    setPage(Math.min(totalPages, Math.max(1, nextPage)));
  };

  const familyMap = useMemo(() => {
    const map = new Map<string, string>();
    families?.forEach((f) => map.set(f.id, f.name));
    return map;
  }, [families]);

  const brandMap = useMemo(() => {
    const map = new Map<string, string>();
    brands?.forEach((b) => map.set(b.id, b.name));
    return map;
  }, [brands]);

  const storefrontPublishedArticleIds = useMemo(() => {
    const ids = new Set<string>();
    storefrontProducts?.forEach((product) => {
      if (product.isPublished) ids.add(product.articleId);
    });
    return ids;
  }, [storefrontProducts]);

  const proPublishedArticleIds = useMemo(() => {
    const ids = new Set<string>();
    proArticlePricing?.forEach((pricing) => ids.add(pricing.articleId));
    return ids;
  }, [proArticlePricing]);

  // Total stock per article
  const stockMap = useMemo(() => {
    const map = new Map<string, number>();
    allStock?.forEach((s) => map.set(s.articleId, (map.get(s.articleId) ?? 0) + s.quantity));
    return map;
  }, [allStock]);

  // Per-warehouse stock: articleId -> { warehouseId: qty }
  const stockPerWarehouse = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    allStock?.forEach((s: ArticleStock) => {
      if (!map.has(s.articleId)) map.set(s.articleId, new Map());
      const wMap = map.get(s.articleId)!;
      wMap.set(s.warehouseId, (wMap.get(s.warehouseId) ?? 0) + s.quantity);
    });
    return map;
  }, [allStock]);

  const articleColumnFilters = useMemo<ColumnTextFilter<Article>[]>(() => {
    const formatArticleBenefit = (article: Article) => {
      const net = article.purchasePriceHt * (1 - (article.supplierDiscount || 0) / 100);
      return net > 0 ? article.salePriceHt - net : '';
    };
    const formatArticleMargin = (article: Article) => {
      const net = article.purchasePriceHt * (1 - (article.supplierDiscount || 0) / 100);
      return net > 0 && article.salePriceHt > 0 ? ((article.salePriceHt - net) / net * 100).toFixed(1) : '';
    };
    return visibleCols.map((key) => {
      const col = ALL_ARTICLE_COLUMNS.find((column) => column.key === key);
      const label = col ? t(col.labelKey) : key;
      switch (key) {
        case 'image':
          return { key, label, disabled: true, getValue: () => '' };
        case 'code':
          return { key, label, getValue: (article) => article.code };
        case 'name':
          return { key, label, getValue: (article) => article.name };
        case 'barcode':
          return { key, label, getValue: (article) => article.barcode };
        case 'category':
          return { key, label, getValue: (article) => article.categoryId ? categoryMap.get(article.categoryId) ?? article.categoryId : '' };
        case 'subCategory':
          return { key, label, getValue: (article) => article.subFamily ? categoryMap.get(article.subFamily) ?? article.subFamily : '' };
        case 'family':
          return { key, label, getValue: (article) => article.family ? familyMap.get(article.family) ?? article.family : '' };
        case 'brand':
          return { key, label, getValue: (article) => article.brand ? brandMap.get(article.brand) ?? article.brand : '' };
        case 'salePriceHt':
          return { key, label, getValue: (article) => article.salePriceHt };
        case 'salePriceTtc':
          return { key, label, getValue: (article) => article.salePriceTtc };
        case 'purchasePriceHt':
          return { key, label, getValue: (article) => article.purchasePriceHt };
        case 'supplierDiscount':
          return { key, label, getValue: (article) => article.supplierDiscount };
        case 'vatRate':
          return { key, label, getValue: (article) => article.vatRate };
        case 'margin':
          return { key, label, getValue: formatArticleMargin };
        case 'benefit':
          return { key, label, getValue: formatArticleBenefit };
        case 'unit':
          return { key, label, getValue: (article) => t(`articles.units.${article.unit}`) };
        case 'stock':
          return { key, label, getValue: (article) => stockMap.get(article.id) ?? 0 };
        case 'isPublishedStorefront':
          return { key, label, getValue: (article) => storefrontPublishedArticleIds.has(article.id) ? t('common.yes') : t('common.no') };
        case 'isPublishedPro':
          return { key, label, getValue: (article) => proPublishedArticleIds.has(article.id) ? t('common.yes') : t('common.no') };
        case 'isActive':
          return { key, label, getValue: (article) => article.isActive ? t('common.active') : t('common.inactive') };
        case 'isInvoiceable':
          return { key, label, getValue: (article) => article.isInvoiceable ? t('common.yes') : t('common.no') };
        default:
          return { key, label, getValue: () => '' };
      }
    });
  }, [brandMap, categoryMap, familyMap, proPublishedArticleIds, stockMap, storefrontPublishedArticleIds, t, visibleCols]);

  const {
    filters: columnFilters,
    setFilter: setColumnFilter,
    filteredRows: articles,
    hasColumnFilters,
    clearColumnFilters,
  } = useColumnTextFilters(pageArticles, articleColumnFilters);

  const toolbarColumnFilters = useMemo(
    () => articleColumnFilters.filter((column) => ['subCategory', 'unit', 'isPublishedStorefront', 'isPublishedPro'].includes(column.key)),
    [articleColumnFilters],
  );

  const handleEdit = (article: Article) => {
    setEditArticle(article);
    setFormOpen(true);
  };

  const handleNew = () => {
    setEditArticle(null);
    setFormOpen(true);
  };

  useOpenCreateFromQuery(handleNew);

  const handleDeleteConfirm = async () => {
    if (articleToDelete) {
      await deleteMutation.mutateAsync(articleToDelete.id);
      setDeleteDialogOpen(false);
      setArticleToDelete(null);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = articles.length > 0 && articles.every((a) => selectedIds.has(a.id));
  const someVisibleSelected = !allVisibleSelected && articles.some((a) => selectedIds.has(a.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        articles.forEach((a) => next.delete(a.id));
        return next;
      }
      const next = new Set(prev);
      articles.forEach((a) => next.add(a.id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const openPrintSingle = (article: Article) => {
    setArticlesToPrint([article]);
    setTicketDialogOpen(true);
  };

  const openPrintSelected = () => {
    const list = articles.filter((a) => selectedIds.has(a.id));
    if (list.length === 0) return;
    setArticlesToPrint(list);
    setTicketDialogOpen(true);
  };

  const hasActiveFilters = Boolean(search || categoryFilter || familyFilter || brandFilter || activeFilter || invoiceableFilter || hasColumnFilters);

  return (
    <div className="min-w-0 max-w-full space-y-6">
      <PageHeader
        title={t('articles.title')}
        actions={
          <Button onClick={handleNew}>
            <Plus data-icon="inline-start" size={16} />
            {t('articles.newArticle')}
          </Button>
        }
      />

      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-primary/5 border-primary/30 px-4 py-2 text-sm">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <span className="font-medium">{t('articles.selectedCount', { count: selectedIds.size })}</span>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              <X size={14} className="me-1.5" />{t('articles.deselectAll')}
            </Button>
          </div>
          <Button size="sm" onClick={openPrintSelected}>
            <Printer size={14} className="me-1.5" />
            {t('articles.printTickets')}
          </Button>
        </div>
      )}

      <Card className="min-w-0 max-w-full transition-all duration-200">
        <CardHeader className="min-w-0 pb-3">
          <FilterBar
            onClear={hasActiveFilters ? () => { setSearch(''); setCategoryFilter(''); setFamilyFilter(''); setBrandFilter(''); setActiveFilter(''); setInvoiceableFilter(''); clearColumnFilters(); } : undefined}
            clearLabel={t('common.clearFilters')}
            rightSlot={
              <Button variant="outline" size="sm" onClick={() => setColConfigOpen(true)}>
                <SlidersHorizontal size={14} className="me-1.5" />
                {t('settings.columns.title')}
              </Button>
            }
          >
            <FilterField label={t('common.search')} grow>
              <div className="relative">
                <Search size={14} className="absolute top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none start-2.5" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('common.search')} className="ps-8 h-8" />
              </div>
            </FilterField>
            {visibleCols.includes('category') && (
              <FilterField label={t('articles.category')} minWidth={200}>
                <SearchableSelect
                  options={(categories ?? []).filter((c) => !c.parentId).map((c) => ({ value: c.id, label: c.name }))}
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  label={t('articles.category')}
                  placeholder={t('common.all')}
                  searchPlaceholder={t('common.search')}
                  className="h-8"
                />
              </FilterField>
            )}
            {visibleCols.includes('family') && (
              <FilterField label={t('articles.family')} minWidth={180}>
                <SearchableSelect
                  options={(families ?? []).map((f) => ({ value: f.id, label: f.name }))}
                  value={familyFilter}
                  onChange={setFamilyFilter}
                  label={t('articles.family')}
                  placeholder={t('common.all')}
                  searchPlaceholder={t('common.search')}
                  className="h-8"
                />
              </FilterField>
            )}
            {visibleCols.includes('brand') && (
              <FilterField label={t('articles.brand')} minWidth={180}>
                <SearchableSelect
                  options={(brands ?? []).map((b) => ({ value: b.id, label: b.name }))}
                  value={brandFilter}
                  onChange={setBrandFilter}
                  label={t('articles.brand')}
                  placeholder={t('common.all')}
                  searchPlaceholder={t('common.search')}
                  className="h-8"
                />
              </FilterField>
            )}
            {visibleCols.includes('isActive') && (
              <FilterField label={t('articles.isActive')} minWidth={170}>
                <SearchableSelect
                  options={[{ value: 'true', label: t('common.active') }, { value: 'false', label: t('common.inactive') }]}
                  value={activeFilter}
                  onChange={setActiveFilter}
                  label={t('articles.isActive')}
                  placeholder={t('common.all')}
                  searchPlaceholder={t('common.search')}
                  className="h-8"
                />
              </FilterField>
            )}
            {visibleCols.includes('isInvoiceable') && (
              <FilterField label={t('articles.isInvoiceable')} minWidth={200}>
                <SearchableSelect
                  options={[{ value: 'true', label: t('common.yes') }, { value: 'false', label: t('common.no') }]}
                  value={invoiceableFilter}
                  onChange={setInvoiceableFilter}
                  label={t('articles.isInvoiceable')}
                  placeholder={t('common.all')}
                  searchPlaceholder={t('common.search')}
                  className="h-8"
                />
              </FilterField>
            )}
            <ColumnTextFilterFields
              rows={pageArticles}
              columns={toolbarColumnFilters}
              filters={columnFilters}
              onFilterChange={setColumnFilter}
              allLabel={t('common.all')}
              searchPlaceholder={t('common.search')}
            />
          </FilterBar>
        </CardHeader>
        <CardContent className="min-w-0 pt-0">

      {/* Table */}
      {(() => {
        const isNumCol = (k: string) => ['salePriceHt', 'salePriceTtc', 'purchasePriceHt', 'supplierDiscount', 'vatRate', 'margin', 'benefit'].includes(k);
        const isCenterCol = (k: string) => ['isActive', 'isInvoiceable', 'isPublishedStorefront', 'isPublishedPro', 'image'].includes(k);
        const isNarrowCol = (k: string) => k === 'image';

        // Stock columns: if visible and multiple warehouses, expand into per-warehouse + total
        const showStock = visibleCols.includes('stock');
        const stockColCount = showStock ? (hasMultipleWarehouses ? (warehouses!.length + 1) : 1) : 0;
        const totalColSpan = visibleCols.filter((k) => k !== 'stock').length + stockColCount + 2; // +1 checkbox, +1 actions

        const renderCell = (article: Article, colKey: string) => {
          switch (colKey) {
            case 'image': {
              const img = article.images?.[0] ?? article.imageUrl;
              return img
                ? <img src={img} alt="" className="w-8 h-8 rounded object-cover" />
                : <div className="w-8 h-8 rounded bg-muted" />;
            }
            case 'code': return article.code;
            case 'name': return <span className="font-medium">{article.name}</span>;
            case 'barcode': return <span className="text-xs text-muted-foreground">{article.barcode ?? '—'}</span>;
            case 'category': return <span className="text-muted-foreground">{article.categoryId ? categoryMap.get(article.categoryId) ?? '—' : '—'}</span>;
            case 'subCategory': return <span className="text-muted-foreground">{article.subFamily ? categoryMap.get(article.subFamily) ?? article.subFamily : '—'}</span>;
            case 'family': return <span className="text-muted-foreground">{article.family ? familyMap.get(article.family) ?? article.family : '—'}</span>;
            case 'brand': return <span className="text-muted-foreground">{article.brand ? brandMap.get(article.brand) ?? article.brand : '—'}</span>;
            case 'salePriceHt': return `${formatPrice(article.salePriceHt)} DT`;
            case 'salePriceTtc': return `${formatPrice(article.salePriceTtc)} DT`;
            case 'purchasePriceHt': return `${formatPrice(article.purchasePriceHt)} DT`;
            case 'supplierDiscount': return article.supplierDiscount > 0 ? <>{article.supplierDiscount}%</> : <span className="text-muted-foreground">—</span>;
            case 'vatRate': return <>{article.vatRate}%</>;
            case 'margin': {
              const net = article.purchasePriceHt * (1 - (article.supplierDiscount || 0) / 100);
              if (net > 0 && article.salePriceHt > 0) {
                const m = ((article.salePriceHt - net) / net * 100);
                return <span className={`${m >= 0 ? 'text-green-600' : 'text-destructive'}`}>{m.toFixed(1)}%</span>;
              }
              return <span className="text-muted-foreground">—</span>;
            }
            case 'benefit': {
              const net = article.purchasePriceHt * (1 - (article.supplierDiscount || 0) / 100);
              const b = article.salePriceHt - net;
              if (net > 0) {
                return <span className={`${b >= 0 ? 'text-green-600' : 'text-destructive'}`}>{b >= 0 ? '+' : ''}{formatPrice(b)} DT</span>;
              }
              return <span className="text-muted-foreground">—</span>;
            }
            case 'unit': return <span className="text-sm">{t(`articles.units.${article.unit}`)}</span>;
            case 'isActive': return article.isActive
              ? <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400">{t('common.active')}</Badge>
              : <Badge variant="outline" className="text-muted-foreground">{t('common.inactive')}</Badge>;
            case 'isInvoiceable': return article.isInvoiceable
              ? <Badge variant="outline" className="border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-400">{t('common.yes')}</Badge>
              : <Badge variant="outline" className="text-muted-foreground">{t('common.no')}</Badge>;
            case 'isPublishedStorefront': return storefrontPublishedArticleIds.has(article.id)
              ? <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400">{t('common.yes')}</Badge>
              : <Badge variant="outline" className="text-muted-foreground">{t('common.no')}</Badge>;
            case 'isPublishedPro': return proPublishedArticleIds.has(article.id)
              ? <Badge variant="outline" className="border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-400">{t('common.yes')}</Badge>
              : <Badge variant="outline" className="text-muted-foreground">{t('common.no')}</Badge>;
            default: return '—';
          }
        };

        const qtyColor = (q: number, minStock: number) => q < 0 ? 'text-destructive' : q <= minStock ? 'text-yellow-600' : '';

        return (
          <div className="min-w-0 max-w-full rounded-lg">
            <Table className="min-w-[720px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b border-border">
                  <TableHead className="h-11 w-10 bg-muted/50 px-3 text-center first:ps-3 sm:px-4 sm:first:ps-5">
                    <Checkbox
                      checked={allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false}
                      onCheckedChange={toggleSelectAll}
                      aria-label={t('articles.selectAll')}
                    />
                  </TableHead>
                  {visibleCols.filter((k) => k !== 'stock').map((key) => {
                    const col = ALL_ARTICLE_COLUMNS.find((c) => c.key === key);
                    if (!col) return null;
                    const alignClass = isNumCol(key) ? 'text-end' : isCenterCol(key) ? 'text-center' : 'text-start';
                    const narrow = isNarrowCol(key) ? 'w-12' : '';
                    return (
                      <TableHead key={key} className={`h-11 bg-muted/50 px-3 text-xs font-medium select-none last:pe-3 sm:px-4 sm:text-sm sm:last:pe-5 ${alignClass} ${narrow}`}>
                        {t(col.labelKey)}
                      </TableHead>
                    );
                  })}
                  {showStock && (hasMultipleWarehouses
                    ? <>{warehouses!.map((w) => <TableHead key={w.id} className="h-11 bg-muted/50 px-3 text-end text-xs font-medium select-none sm:px-4 sm:text-sm">{w.name}</TableHead>)}<TableHead className="h-11 bg-muted/50 px-3 text-end text-xs font-semibold select-none sm:px-4 sm:text-sm">{t('common.total')}</TableHead></>
                    : <TableHead className="h-11 bg-muted/50 px-3 text-end text-xs font-medium select-none sm:px-4 sm:text-sm">{t('stock.currentStock')}</TableHead>
                  )}
                  <TableHead className="h-11 w-10 bg-muted/50 px-3 last:pe-3 sm:px-4 sm:last:pe-5" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {hasLoadError ? (
                  <TableRow className="border-0">
                    <TableCell colSpan={totalColSpan} className="px-4 py-10 text-center text-sm text-destructive">
                      {t('common.errorLoading')}
                    </TableCell>
                  </TableRow>
                ) : isCheckingArticles || (totalArticles > 0 && pageArticles.length === 0) ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={`sk-${i}`} className="border-b border-border/60 last:border-b-0">
                      {Array.from({ length: totalColSpan }).map((_, j) => (
                        <TableCell key={j} className="px-3 py-2 sm:px-4 sm:py-3">
                          <Skeleton className="h-4 w-full max-w-[160px] bg-muted/70" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : totalArticles === 0 || articles.length === 0 ? (
                  <TableRow className="border-0">
                    <TableCell colSpan={totalColSpan} className="px-4 py-10 text-center">
                      <EmptyState
                        icon={<Package size={18} />}
                        title={t('common.noResults')}
                        action={<Button variant="outline" size="sm" onClick={handleNew}>
                            <Plus data-icon="inline-start" size={14} />
                            {t('articles.newArticle')}
                          </Button>
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : articles.map((article) => {
                  const totalQty = stockMap.get(article.id) ?? 0;
                  const perWh = stockPerWarehouse.get(article.id);
                  const isSelected = selectedIds.has(article.id);
                  return (
                    <TableRow
                      key={article.id}
                      className={`border-b border-border/60 last:border-b-0 transition-colors cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-primary/5' : ''}`}
                      onClick={() => navigate(`/articles/${article.id}`)}
                    >
                      <TableCell className="px-3 py-2 text-center first:ps-3 sm:px-4 sm:py-3 sm:first:ps-5" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(article.id)}
                          aria-label={t('articles.selectRow')}
                        />
                      </TableCell>
                      {visibleCols.filter((k) => k !== 'stock').map((key) => (
                        <TableCell key={key} className={`px-3 py-2 text-xs last:pe-3 sm:px-4 sm:py-3 sm:text-sm sm:last:pe-5 ${isNumCol(key) ? 'text-end' : isCenterCol(key) ? 'text-center' : ''}`}>
                          {renderCell(article, key)}
                        </TableCell>
                      ))}
                      {showStock && (hasMultipleWarehouses ? (
                        <>{warehouses!.map((w) => {
                          const q = perWh?.get(w.id) ?? 0;
                          return <TableCell key={w.id} className="px-3 py-2 text-xs sm:px-4 sm:py-3 sm:text-sm text-end"><span className={`${qtyColor(q, article.stockMin)}`}>{q}</span></TableCell>;
                        })}<TableCell className="px-3 py-2 text-xs sm:px-4 sm:py-3 sm:text-sm text-end"><span className={`font-semibold ${qtyColor(totalQty, article.stockMin)}`}>{totalQty}</span></TableCell></>
                      ) : (
                        <TableCell className="px-3 py-2 text-xs sm:px-4 sm:py-3 sm:text-sm text-end"><span className={`${qtyColor(totalQty, article.stockMin)}`}>{totalQty}</span></TableCell>
                      ))}
                      <TableCell className="px-3 py-2 sm:px-4 sm:py-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon-xs"><MoreHorizontal size={14} /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(article); }}>
                              <Pencil size={14} />{t('common.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openPrintSingle(article); }}>
                              <Printer size={14} />{t('articles.printTicket')}
                            </DropdownMenuItem>
                            <DropdownMenuItem variant="destructive" onClick={(e) => { e.stopPropagation(); setArticleToDelete(article); setDeleteDialogOpen(true); }}>
                              <Trash2 size={14} />{t('common.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="flex flex-col gap-3 border-t border-border px-3 py-3 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-5">
              <div>
                {isCheckingArticles
                  ? t('common.checkingData')
                  : t('articles.paginationSummary', { start: pageStart, end: pageEnd, total: totalArticles })}
                {!isCheckingArticles && isFetching && <span className="ms-2">{t('common.loading')}</span>}
                {!isCheckingArticles && isCountFetching && <span className="ms-2">{t('common.checkingData')}</span>}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1 || isCheckingArticles}
                >
                  <ChevronLeft data-icon="inline-start" size={14} className="rtl:rotate-180" />
                  {t('common.previousPage')}
                </Button>
                <span className="min-w-24 text-center">{t('common.pageOf', { page, totalPages })}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages || isCheckingArticles}
                >
                  {t('common.nextPage')}
                  <ChevronRight data-icon="inline-end" size={14} className="rtl:rotate-180" />
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <ArticleFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        article={editArticle}
      />

      {/* Ticket print dialog */}
      <ArticleTicketPrintDialog
        open={ticketDialogOpen}
        onClose={() => setTicketDialogOpen(false)}
        articles={articlesToPrint}
      />

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('articles.deleteConfirm')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{t('articles.deleteWarning')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column config dialog */}
      <Dialog open={colConfigOpen} onOpenChange={setColConfigOpen}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>{t('settings.columns.title')}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pe-1">
            {/* Visible columns — draggable */}
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground">{t('settings.columns.visible')}</p>
              {visibleCols.map((key, idx) => {
                const col = ALL_ARTICLE_COLUMNS.find((c) => c.key === key);
                if (!col) return null;
                const isDragging = dragIdx === idx;
                const isDropTarget = dragOverIdx === idx && dragIdx !== null && dragIdx !== idx;
                return (
                  <div key={key} className="relative">
                    {/* Drop indicator line above */}
                    {isDropTarget && dragIdx !== null && dragIdx > idx && (
                      <div className="absolute -top-0.5 left-0 right-0 h-0.5 bg-primary rounded-full" />
                    )}
                    <div
                      draggable={!col.locked}
                      onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; }}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverIdx(idx); }}
                      onDragLeave={() => setDragOverIdx(null)}
                      onDrop={() => { if (dragIdx !== null && dragIdx !== idx) moveCol(dragIdx, idx); setDragIdx(null); setDragOverIdx(null); }}
                      onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                      className={`flex items-center justify-between rounded-md border px-2 py-1.5 text-sm transition-all ${isDragging ? 'opacity-30 scale-95' : ''} ${isDropTarget ? 'border-primary bg-primary/5' : ''} ${col.locked ? 'bg-muted/30' : 'cursor-grab active:cursor-grabbing hover:bg-accent/50'}`}
                    >
                      <div className="flex items-center gap-2">
                        {!col.locked ? <GripVertical size={14} className="text-muted-foreground/40" /> : <div className="w-3.5" />}
                        <span>{t(col.labelKey)}</span>
                      </div>
                      {!col.locked && (
                        <Button variant="ghost" size="icon-xs" onClick={() => toggleCol(key)} className="text-muted-foreground hover:text-foreground">
                          <EyeOff size={14} />
                        </Button>
                      )}
                    </div>
                    {/* Drop indicator line below */}
                    {isDropTarget && dragIdx !== null && dragIdx < idx && (
                      <div className="absolute -bottom-0.5 left-0 right-0 h-0.5 bg-primary rounded-full" />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Hidden columns */}
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground">{t('settings.columns.hidden')}</p>
              {ALL_ARTICLE_COLUMNS.filter((c) => !c.locked && !visibleCols.includes(c.key)).length > 0 ? (
                ALL_ARTICLE_COLUMNS.filter((c) => !c.locked && !visibleCols.includes(c.key)).map((col) => (
                  <div
                    key={col.key}
                    className="flex items-center justify-between rounded-md border border-dashed px-2 py-1.5 text-sm text-muted-foreground cursor-pointer hover:bg-accent"
                    onClick={() => toggleCol(col.key)}
                  >
                    <span>{t(col.labelKey)}</span>
                    <Eye size={14} />
                  </div>
                ))
              ) : (
                <div className="rounded-md border border-dashed px-2 py-1.5 text-sm text-muted-foreground">
                  {t('settings.columns.allVisible')}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={resetCols}>
              <RotateCcw size={14} className="me-1.5" />{t('settings.columns.resetDefault')}
            </Button>
            <Button size="sm" onClick={() => setColConfigOpen(false)}>{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
